import { AppBundle } from 'api/optimalprint-sdk.d';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Store } from 'store';
import { setCurrentProductAction, setProductIdAction } from '../slice';

const SIZE_SELECTOR_NAME = 'UnifiedPaperFormat';
const SIZE_CANVAS_SELECTOR_NAME = 'UnifiedCanvasFormat';
const ORIENTATION_TYPENAME = 'Orientation';

type ProductItem = AppBundle.Api.Entity.App.V1.AbstractProduct.IProductItem;

const getAspectRatio = (value: string) => {
  const productSizes = value.split('-')[0].split('x');
  if (productSizes.length !== 2) return 0;
  return Number(productSizes[0]) / Number(productSizes[1]);
};

const getSizeAttrValue = (product: ProductItem, typeName: string) => {
  return product.attributes?.find((attr) => attr.typeName === typeName)?.value || '1x1000-';
};

function selectClosestAspectRatioProduct(products: ProductItem[], typeName: string, imageAspectRatio: number) {
  return products.reduce((result, curr) => {
    const sizeAttrValue = getSizeAttrValue(curr, typeName);

    return Math.abs(getAspectRatio(sizeAttrValue) - imageAspectRatio) <
      Math.abs(getAspectRatio(getSizeAttrValue(result, typeName)) - imageAspectRatio)
      ? curr
      : result;
  });
}

const isSquaredProduct = (aspectRatio: number) => Math.abs(1 - aspectRatio) < 0.05;

const filterProductByOrientation = (products: ProductItem[], imageAspectRatio: number) => {
  const targetOrientation = imageAspectRatio < 1 ? 'ver' : 'hor';
  return products.filter(
    (product) =>
      product.attributes?.find((attr) => attr.typeName === ORIENTATION_TYPENAME)?.value === targetOrientation,
  );
};

const pickBetterImageFormatOperation =
  (imageAspectRatio: number, formatPickedCallback: () => void) =>
  (dispatch: ThunkDispatch<Store, undefined, AnyAction>, getState: () => Store) => {
    const store = getState();
    const { selectedAbstractProduct } = store.publicAssetProduct;

    const productPageSelectors = selectedAbstractProduct?.productPageSelectors || [];
    const sizeSelector =
      productPageSelectors.find((selector) => selector.typeName === SIZE_SELECTOR_NAME) ||
      productPageSelectors.find((selector) => selector.typeName === SIZE_CANVAS_SELECTOR_NAME);

    if (!sizeSelector || !selectedAbstractProduct?.products) {
      return;
    }
    const bestMatchingProduct = selectClosestAspectRatioProduct(
      isSquaredProduct(imageAspectRatio)
        ? selectedAbstractProduct?.products
        : filterProductByOrientation(selectedAbstractProduct?.products, imageAspectRatio),
      sizeSelector.typeName,
      imageAspectRatio,
    );

    dispatch(setProductIdAction(bestMatchingProduct.productId));
    dispatch(setCurrentProductAction(bestMatchingProduct));

    formatPickedCallback();
  };

export default pickBetterImageFormatOperation;
